type Direction = "Up" | "Down" | "Left" | "Right";
type OtherAction =
  | "Move"
  | "Start"
  | "Pause"
  | "UpUp"
  | "DownUp"
  | "LeftUp"
  | "RightUp";
export type Action = Direction | OtherAction;

type Pos = [number, number];
export type Snek = Pos[];

const WIDTH = 20;
const HEIGHT = 20;
const START_SPEED = 500;
const SPEEDUP_FACTOR = 0.95;

const overlap = ([x, y]: number[], snek: Snek) =>
  snek.some(([sx, sy]) => x == sx && y == sy);

export const randomFreePosition = (snek: Snek, W = WIDTH, H = HEIGHT) => {
  const freePositions = [...Array(W)]
    .flatMap((_, x) => [...Array(H)].map((_, y) => [x, y]))
    .filter((pos) => !overlap(pos, snek));
  return freePositions[Math.floor(Math.random() * freePositions.length)] as Pos;
};

type State = "Waiting" | "Running" | "Paused" | "Crashed";

export type GameState = {
  tick: number;
  delay: number;
  state: State;
  score: number;
  direction: Direction;
  nextDirection: Direction[];
  speedUp: boolean;
  holdDown: boolean;
  holdDownStart: number;
  width: number;
  height: number;
  snek: Snek;
  apple: Pos;
};

export const initState = (overrides: Partial<GameState> = {}): GameState => {
  const snek: Snek = [
    [7, 10],
    [7, 10],
    [7, 10],
    [7, 10],
  ];
  return {
    tick: 0,
    delay: START_SPEED,
    state: "Waiting",
    score: 0,
    direction: "Right",
    nextDirection: [],
    speedUp: false,
    holdDown: false,
    holdDownStart: 0,
    width: WIDTH,
    height: HEIGHT,
    snek: snek,
    apple: randomFreePosition(snek),
    ...overrides,
  };
};

const deltas: Record<Direction, Pos> = {
  Up: [0, -1],
  Down: [0, 1],
  Left: [-1, 0],
  Right: [1, 0],
};

const legalTurns: Record<Direction, Direction[]> = {
  Up: ["Left", "Right"],
  Down: ["Left", "Right"],
  Left: ["Up", "Down"],
  Right: ["Up", "Down"],
};

const crashed = (snek: Snek) => {
  const head = snek[0];
  return (
    head[0] < 0 ||
    head[1] < 0 ||
    head[0] >= WIDTH ||
    head[1] >= HEIGHT ||
    overlap(head, snek.slice(1))
  );
};

const moveTail = ([hx, hy]: Pos, [tx, ty]: Pos): Pos => {
  const dx = hx - tx,
    dy = hy - ty,
    adx = Math.abs(dx),
    ady = Math.abs(dy),
    ndx = adx == 2 ? dx / 2 : ady == 2 ? dx : 0,
    ndy = ady == 2 ? dy / 2 : adx == 2 ? dy : 0;
  return [tx + ndx, ty + ndy];
};

export const reducer = (state: GameState, action: Action): GameState => {
  switch (action) {
    case "Start":
      return state.state == "Crashed"
        ? { ...initState(), state: "Running" }
        : { ...state, state: "Running" };

    case "Pause":
      return state.state == "Running"
        ? { ...state, state: "Paused" }
        : state.state == "Paused"
        ? { ...state, state: "Running" }
        : state;

    case "Up":
    case "Down":
    case "Left":
    case "Right": {
      if (state.nextDirection.length >= 2) {
        return state;
      }
      const fromDirection =
        state.nextDirection.length > 0
          ? state.nextDirection[0]
          : state.direction;
      return {
        ...state,
        holdDown: action == state.direction,
        nextDirection: legalTurns[fromDirection].includes(action)
          ? [...state.nextDirection, action]
          : state.nextDirection,
      };
    }

    case "UpUp":
    case "DownUp":
    case "LeftUp":
    case "RightUp":
      return { ...state, holdDown: false };

    case "Move": {
      if (state.state != "Running") {
        return state;
      }

      const dir =
        state.nextDirection.length > 0
          ? state.nextDirection[0]
          : state.direction;
      const nextDir =
        state.nextDirection.length > 0
          ? state.nextDirection.slice(1)
          : state.nextDirection;

      const newHead: Pos = [
        state.snek[0][0] + deltas[dir][0],
        state.snek[0][1] + deltas[dir][1],
      ];

      const movedSnek: Snek = state.snek
        .slice(1)
        .reduce((sn, s, i) => [...sn, moveTail(sn[i], s)], [newHead]);

      const nextState = {
        ...state,
        snek: movedSnek,
        holdDownStart:
          state.holdDown && dir == state.direction
            ? state.holdDownStart
            : Date.now(),
        speedUp: state.holdDown && Date.now() - state.holdDownStart > 100,
        tick: state.tick + 1,
        direction: dir,
        nextDirection: nextDir,
      };

      if (crashed(movedSnek)) {
        return { ...state, state: "Crashed" };
      } else if (
        state.apple[0] == movedSnek[0][0] &&
        state.apple[1] == movedSnek[0][1]
      ) {
        const eatenSnek: Snek = [newHead, ...state.snek];
        return {
          ...nextState,
          snek: eatenSnek,
          score: state.score + 1,
          apple: randomFreePosition(eatenSnek),
          delay: state.delay * SPEEDUP_FACTOR,
        };
      } else {
        return nextState;
      }
    }
  }
};
